import React from "react";

export const ENTITLEMENT_TYPES = [
  { value: "SaaS", label: "SaaS", unit: "Months" },
  { value: "SmsSegments", label: "SMS", unit: "Credits" },
  { value: "Hardware", label: "Hardware", unit: "Units" },
];

export function defaultTitleForType(type) {
  return ENTITLEMENT_TYPES.find((t) => t.value === type)?.label || type || "";
}

/** Display label for invoices / includes lists — prefers custom title. */
export function entitlementDisplayTitle(ent) {
  const title = (ent?.entitlementTitle || ent?.EntitlementTitle || "").trim();
  if (title) return title;
  const type = ent?.entitlementType || ent?.EntitlementType || "";
  return defaultTitleForType(type) || type;
}

export function emptyEntitlement(sortOrder = 0) {
  return {
    entitlementTitle: defaultTitleForType("SaaS"),
    entitlementType: "SaaS",
    quantity: 1,
    unit: "Months",
    unitPrice: "",
    sortOrder,
  };
}

export function sumEntitlementPrices(entitlements) {
  return (entitlements || []).reduce((sum, e) => {
    const price = parseFloat(e.unitPrice);
    return sum + (Number.isNaN(price) || price < 0 ? 0 : price);
  }, 0);
}

export function mapApiEntitlements(list) {
  if (!Array.isArray(list) || list.length === 0) return [emptyEntitlement(0)];
  return list.map((e, idx) => {
    const type = e.entitlementType || e.EntitlementType || "SaaS";
    const title =
      e.entitlementTitle || e.EntitlementTitle || defaultTitleForType(type);
    return {
      entitlementTitle: title,
      entitlementType: type,
      quantity: e.quantity ?? e.Quantity ?? 0,
      unit: e.unit || e.Unit || "",
      unitPrice: e.unitPrice ?? e.UnitPrice ?? "",
      sortOrder: e.sortOrder ?? e.SortOrder ?? idx,
    };
  });
}

export function toApiEntitlements(entitlements) {
  return (entitlements || [])
    .filter((e) => e.entitlementType)
    .map((e, idx) => {
      const typeMeta = ENTITLEMENT_TYPES.find((t) => t.value === e.entitlementType);
      const title = (e.entitlementTitle || "").trim() || defaultTitleForType(e.entitlementType);
      return {
        EntitlementTitle: title,
        EntitlementType: e.entitlementType,
        Quantity: parseInt(e.quantity, 10) || 0,
        Unit: e.unit || typeMeta?.unit || "",
        UnitPrice: Math.round((parseFloat(e.unitPrice) || 0) * 100) / 100,
        SortOrder: e.sortOrder ?? idx,
      };
    });
}

function PackageEntitlementsEditor({
  entitlements,
  onChange,
  inputStyle,
  labelColor,
  mutedColor,
  themeColor,
  isDarkMode,
}) {
  const total = sumEntitlementPrices(entitlements);

  const updateRow = (index, field, value) => {
    const next = entitlements.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [field]: value };
      if (field === "entitlementType") {
        const meta = ENTITLEMENT_TYPES.find((t) => t.value === value);
        updated.unit = meta?.unit || "";
        const prevDefault = defaultTitleForType(row.entitlementType);
        const currentTitle = (row.entitlementTitle || "").trim();
        // Keep custom titles; refresh when title was empty or still the previous type default
        if (!currentTitle || currentTitle === prevDefault) {
          updated.entitlementTitle = meta?.label || value;
        }
      }
      return updated;
    });
    onChange(next);
  };

  const addRow = () => {
    onChange([...entitlements, emptyEntitlement(entitlements.length)]);
  };

  const removeRow = (index) => {
    if (entitlements.length <= 1) {
      onChange([emptyEntitlement(0)]);
      return;
    }
    onChange(entitlements.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div
        className="alert mb-3"
        style={{
          backgroundColor: isDarkMode ? "rgba(32, 201, 151, 0.2)" : "#d1ecf1",
          borderColor: isDarkMode ? "rgba(32, 201, 151, 0.3)" : "#bee5eb",
          color: isDarkMode ? "#e0e0e0" : "#0c5460",
        }}
      >
        <i className="fa fa-info-circle me-2"></i>
        <strong>Title</strong> is shown on invoices and in the UI.{" "}
        <strong>Type</strong> drives subscription / wallet auto-apply. Package
        sell price (ex-VAT) is the sum of entitlement prices.
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-2">
          <thead>
            <tr>
              <th style={labelColor}>Title</th>
              <th style={labelColor}>Type</th>
              <th style={labelColor}>Qty</th>
              <th style={labelColor}>Unit</th>
              <th style={labelColor}>Unit price (£)</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {entitlements.map((row, index) => (
              <tr key={index}>
                <td style={{ minWidth: 160 }}>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={150}
                    placeholder="Display title"
                    value={row.entitlementTitle || ""}
                    onChange={(e) =>
                      updateRow(index, "entitlementTitle", e.target.value)
                    }
                    style={inputStyle}
                  />
                </td>
                <td>
                  <select
                    className="form-control"
                    value={row.entitlementType}
                    onChange={(e) =>
                      updateRow(index, "entitlementType", e.target.value)
                    }
                    style={inputStyle}
                  >
                    {ENTITLEMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ minWidth: 90 }}>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    value={row.quantity}
                    onChange={(e) => updateRow(index, "quantity", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={row.unit}
                    readOnly
                    style={{ ...inputStyle, opacity: 0.8 }}
                  />
                </td>
                <td style={{ minWidth: 120 }}>
                  <input
                    type="number"
                    className="form-control"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={row.unitPrice}
                    onChange={(e) => updateRow(index, "unitPrice", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeRow(index)}
                    title="Remove"
                  >
                    <i className="fa fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-sm"
          onClick={addRow}
          style={{
            backgroundColor: themeColor,
            color: "#fff",
            border: "none",
            borderRadius: 8,
          }}
        >
          <i className="fa fa-plus me-1" />
          Add entitlement
        </button>
        <div style={{ ...labelColor, fontWeight: 600, fontSize: 16 }}>
          Package total (ex-VAT):{" "}
          <span style={{ color: themeColor }}>£{total.toFixed(2)}</span>
          <div style={{ ...mutedColor, fontWeight: 400, fontSize: 12 }}>
            Duration comes from SaaS quantity (months)
          </div>
        </div>
      </div>
    </div>
  );
}

export default PackageEntitlementsEditor;
